import htmx from 'htmx.org';
// Importing the component pins evaluation order: multiSelectGroup.js awaits the translation catalog
// and self-initializes ahead of this module, so its instances exist by the time initPage() runs.
import {MultiSelectGroup} from '../../components/multiSelectGroup.js';
import {requestGeolocation} from '../../components/localGroupsMap/geolocation.js';
import {isMobile} from '../../modules/breakpoint.js';

const RESULTS_SELECTOR = '#meetup-search-results';
const PAGINATION_LINK_SELECTOR = `${RESULTS_SELECTOR} nav a`;

/**
 * Force a fresh render when the page is restored from the browser's back/forward cache.
 * The bfcache keeps the JS-mutated DOM (multi-select pills, reserved padding) but re-runs
 * component init on top of it, which produces a distorted layout until a manual refresh.
 */
function reloadOnBackForwardCacheRestore() {
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            window.location.reload();
        }
    });
}

reloadOnBackForwardCacheRestore();
collapseMobileCollapsibleDetails();

/** Init the Near Me toggle and its geolocation flow. */
function initNearMeToggle() {
    const checkbox = document.getElementById('nearme');
    if (!(checkbox instanceof HTMLInputElement) || checkbox.type !== 'checkbox') {
        return;
    }

    const hiddenInput = document.querySelector('.js-nearMeHiddenInputField');
    if (!(hiddenInput instanceof HTMLInputElement)) {
        return;
    }

    const form = checkbox.closest('form');
    if (!(form instanceof HTMLFormElement)) {
        return;
    }

    const syncHiddenState = () => {
        hiddenInput.disabled = checkbox.checked;
    };

    /**
     * @param {number|null} lat
     * @param {number|null} lng
     */
    const writeCoordinateInputs = (lat, lng) => {
        upsertHiddenInput(form, 'userlat', lat === null ? '' : String(lat));
        upsertHiddenInput(form, 'userlng', lng === null ? '' : String(lng));
    };

    checkbox.addEventListener('change', (event) => {
        syncHiddenState();
        toggleLocationFieldsDisabled(checkbox.checked);

        if (!checkbox.checked) {
            writeCoordinateInputs(null, null);
            return;
        }

        event.stopImmediatePropagation();

        requestGeolocation(
            ({lat, lng}) => {
                writeCoordinateInputs(lat, lng);
                htmx.trigger(form, 'submit');
            },
            () => {
                writeCoordinateInputs(null, null);
                htmx.trigger(form, 'submit');
            }
        );
    }, true);

    syncHiddenState();
    toggleLocationFieldsDisabled(checkbox.checked);
}

/** Init the Show Past toggle. */
function initShowPastToggle() {
    const checkbox = document.getElementById('showpast');
    if (!(checkbox instanceof HTMLInputElement) || checkbox.type !== 'checkbox') {
        return;
    }

    const hiddenInput = document.querySelector('.js-showPastHiddenInputField');
    if (!(hiddenInput instanceof HTMLInputElement)) {
        return;
    }

    const syncHiddenState = () => {
        hiddenInput.disabled = checkbox.checked;
    };

    checkbox.addEventListener('change', syncHiddenState);
    syncHiddenState();
}

/** Auto-enable the Show Past toggle when a past "from" date is chosen, so the UI matches the results. */
function initFromDateShowPastSync() {
    const fromInput = document.getElementById('from');
    if (!(fromInput instanceof HTMLInputElement)) {
        return;
    }

    const checkbox = document.getElementById('showpast');
    const hiddenInput = document.querySelector('.js-showPastHiddenInputField');
    if (!(checkbox instanceof HTMLInputElement) || !(hiddenInput instanceof HTMLInputElement)) {
        return;
    }

    fromInput.addEventListener('change', () => {
        if (fromInput.value === '' || checkbox.checked) {
            return;
        }

        // Compare YYYY-MM-DD strings (lexicographic order == chronological) to stay timezone-agnostic.
        const now = new Date();
        const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        if (fromInput.value >= todayIso) {
            return;
        }

        // Sync the hidden input directly (mirroring initShowPastToggle) rather than dispatching a
        // change: the date input already triggers the HTMX search, so a second event would double-submit.
        checkbox.checked = true;
        hiddenInput.disabled = true;
    });
}

/**
 * The instance multiSelectGroup.js attaches to a `[data-multi-select-group]` element.
 * @param {Element} element
 * @returns {MultiSelectGroup}
 */
function multiSelectGroupOf(element) {
    const instance = /** @type {Element & {multiSelectGroupInstance?: MultiSelectGroup}} */ (element).multiSelectGroupInstance;
    if (!(instance instanceof MultiSelectGroup)) {
        throw new Error(`Multi-select-group "${element.getAttribute('data-tags-input')}" is not initialized.`);
    }

    return instance;
}

/** Narrow the Country dropdown to the countries of the selected continent(s). */
function initContinentCountryFilter() {
    const continentsElement = document.querySelector('[data-multi-select-group][data-tags-input="continents"]');
    const countriesElement = document.querySelector('[data-multi-select-group][data-tags-input="countries"]');
    if (!(continentsElement instanceof HTMLElement) || !(countriesElement instanceof HTMLElement)) {
        return;
    }

    const continents = multiSelectGroupOf(continentsElement);
    const countries = multiSelectGroupOf(countriesElement);
    const applyFilter = () => {
        countries.setGroupFilter(continents.getValues());
    };

    continentsElement.addEventListener('multiSelectGroup:change', applyFilter);
    applyFilter();
}

/** Init the removable local-group (city) filter chip applied via the "View All Meetups in [City]" deep link. */
function initGroupFilterChip() {
    const section = document.querySelector('[data-meetup-filter-group]');
    if (!(section instanceof HTMLElement)) {
        return;
    }

    const form = section.closest('form');
    if (!(form instanceof HTMLFormElement)) {
        return;
    }

    const removeButton = section.querySelector('.js-removeGroupFilter');
    removeButton?.addEventListener('click', () => {
        // Removing the section drops its "Showing" heading and the hidden "group" input together.
        section.remove();
        htmx.trigger(form, 'submit');
    });

    // Reset must also drop the city scope. Remove the section synchronously (capture phase) so the
    // reset request never serializes the "group" input.
    form.querySelector('.js-resetFiltersButton')?.addEventListener('click', () => {
        section.remove();
    }, true);
}

/** @param {boolean} disabled */
function toggleLocationFieldsDisabled(disabled) {
    const container = document.querySelector('[data-meetup-location-fields]');
    if (!(container instanceof HTMLElement)) {
        return;
    }

    container.dataset.disabled = disabled ? 'true' : 'false';
    container.querySelectorAll('input, button').forEach((element) => {
        if (element instanceof HTMLElement) {
            element.toggleAttribute('disabled', disabled);
        }
    });
}

/**
 * Insert or update a hidden input on the form.
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 */
function upsertHiddenInput(form, name, value) {
    let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.append(input);
    }
    input.value = value;
    if (value === '') {
        input.disabled = true;
    } else {
        input.disabled = false;
    }
}

/**
 * Checks whether any filter is at a non-default value.
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function hasActiveFilters(form) {
    const typeRadio = form.querySelector('input[name="type"]:checked');
    if (typeRadio instanceof HTMLInputElement && typeRadio.value !== form.dataset.defaultType) {
        return true;
    }

    const sortRadio = form.querySelector('input[name="sort"]:checked');
    if (sortRadio instanceof HTMLInputElement && sortRadio.value !== form.dataset.defaultSort) {
        return true;
    }

    const nearMe = form.querySelector('#nearme');
    if (nearMe instanceof HTMLInputElement && nearMe.checked) {
        return true;
    }

    const showPast = form.querySelector('#showpast');
    if (showPast instanceof HTMLInputElement && showPast.checked) {
        return true;
    }

    // The group filter is represented by a chip that is removed (not disabled) when cleared, so presence is enough.
    if (form.querySelector('[data-group-filter-input]') instanceof HTMLInputElement) {
        return true;
    }

    const continentsInput = form.querySelector('input[name="continents"][data-multi-select-hidden-input]');
    if (continentsInput instanceof HTMLInputElement && continentsInput.value.trim() !== '') {
        return true;
    }

    const countriesInput = form.querySelector('input[name="countries"][data-multi-select-hidden-input]');
    if (countriesInput instanceof HTMLInputElement && countriesInput.value.trim() !== '') {
        return true;
    }

    return false;
}

/** Init the Reset Filters button. */
function initResetFiltersButton() {
    const resetButton = document.querySelector('.js-resetFiltersButton');
    if (!(resetButton instanceof HTMLButtonElement)) {
        return;
    }

    const form = resetButton.closest('form');
    if (!(form instanceof HTMLFormElement)) {
        return;
    }

    const updateResetButtonState = () => {
        resetButton.disabled = !hasActiveFilters(form);
    };

    const resetFiltersToDefaults = () => {
        const defaultTypeRadio = form.querySelector(`input[name="type"][value="${form.dataset.defaultType}"]`);
        if (defaultTypeRadio instanceof HTMLInputElement) {
            defaultTypeRadio.checked = true;
        }

        const defaultSortRadio = form.querySelector(`input[name="sort"][value="${form.dataset.defaultSort}"]`);
        if (defaultSortRadio instanceof HTMLInputElement) {
            defaultSortRadio.checked = true;
        }

        form.querySelectorAll('[data-multi-select-group]').forEach((element) => {
            multiSelectGroupOf(element).clear();
        });

        const nearMe = form.querySelector('#nearme');
        if (nearMe instanceof HTMLInputElement) {
            nearMe.checked = false;
            nearMe.dispatchEvent(new Event('change', {bubbles: true}));
        }

        const showPast = form.querySelector('#showpast');
        if (showPast instanceof HTMLInputElement) {
            showPast.checked = false;
        }
        const showPastHidden = form.querySelector('.js-showPastHiddenInputField');
        if (showPastHidden instanceof HTMLInputElement) {
            showPastHidden.disabled = false;
        }

        upsertHiddenInput(form, 'userlat', '');
        upsertHiddenInput(form, 'userlng', '');

        // The group chip is removed by initGroupFilterChip's capture-phase reset handler (before the request serializes).
        toggleLocationFieldsDisabled(false);
        updateResetButtonState();
    };

    resetButton.addEventListener('htmx:afterRequest', (event) => {
        // @ts-ignore — htmx event detail
        if (event.detail?.successful) {
            resetFiltersToDefaults();
        }
    });
    form.addEventListener('change', updateResetButtonState);
    form.addEventListener('input', updateResetButtonState);
}

/** Smoothly scroll the search results container into view. */
function scrollResultsIntoView() {
    const resultsContainer = document.getElementById('meetup-search-results');
    if (!(resultsContainer instanceof HTMLElement)) {
        return;
    }

    resultsContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
    });
}

/** Remove userLat/userLng from the visible URL so bookmarks and shared links don't carry the visitor's coordinates. */
function scrubGeoParametersFromUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('userlat') && !url.searchParams.has('userlng')) {
        return;
    }
    url.searchParams.delete('userlat');
    url.searchParams.delete('userlng');
    history.replaceState(null, '', url.toString());
}

/** Collapse mobile-collapsible details on small viewports. */
function collapseMobileCollapsibleDetails() {
    if (!isMobile()) {
        return;
    }

    document.querySelectorAll('details[data-mobile-collapsible][open]').forEach((details) => {
        if (details instanceof HTMLDetailsElement) {
            details.open = false;
        }
    });
}

/** Wire up filter controls and HTMX event handlers. */
function initPage() {
    initNearMeToggle();
    initShowPastToggle();
    initFromDateShowPastSync();
    initContinentCountryFilter();
    initGroupFilterChip();
    initResetFiltersButton();

    let shouldSmoothScrollResultsOnNextSettle = false;

    document.body.addEventListener('htmx:beforeRequest', (event) => {
        // @ts-ignore — htmx event detail
        const requestElement = event.detail?.elt;
        if (!(requestElement instanceof HTMLElement)) {
            return;
        }
        shouldSmoothScrollResultsOnNextSettle = Boolean(requestElement.closest(PAGINATION_LINK_SELECTOR));
    });

    document.body.addEventListener('htmx:afterSettle', (event) => {
        // @ts-ignore — htmx event detail
        const target = event.detail?.target;
        if (target instanceof HTMLElement && target.id === 'meetup-search-results') {
            scrubGeoParametersFromUrl();
        }
        if (shouldSmoothScrollResultsOnNextSettle && target instanceof HTMLElement && target.id === 'meetup-search-results') {
            shouldSmoothScrollResultsOnNextSettle = false;
            scrollResultsIntoView();
        }
    });

    scrubGeoParametersFromUrl();

    document.body.addEventListener('htmx:responseError', () => {
        shouldSmoothScrollResultsOnNextSettle = false;
    });

    document.body.addEventListener('htmx:sendError', () => {
        shouldSmoothScrollResultsOnNextSettle = false;
    });
}

document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initPage) : initPage();
